import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Avatar,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const OtpVerification = ({ username, message, onVerify, onBack }) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate OTP input
    if (!otp.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Format OTP (lowercase, trim whitespace)
      const formattedOtp = otp.toLowerCase().trim();

      // Call the verification function
      await onVerify(username, formattedOtp);

      // Success is handled by parent component
    } catch (err) {
      // Handle verification errors
      if (err.attemptsRemaining !== undefined) {
        setAttemptsRemaining(err.attemptsRemaining);
        setError(`${err.message || 'Invalid verification code'} (${err.attemptsRemaining} attempts remaining)`);
      } else if (err.requiresLogin) {
        // Too many failed attempts - redirect to login
        setError(err.message || 'Too many failed attempts. Please login again.');
        setTimeout(() => {
          onBack();
        }, 2000);
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setOtp('');
    setError('');
    onBack();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/wygc-bg.svg)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundColor: 'rgba(255, 255, 255, 0)',
        backgroundBlendMode: 'lighten'
      }}
    >
      <Card sx={{ minWidth: 300, maxWidth: 450, boxShadow: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          p={3}
          bgcolor="warning.main"
          color="warning.contrastText"
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              border: '2px solid white',
              boxShadow: 2,
              mb: 2,
              bgcolor: 'white',
              color: 'warning.main'
            }}
          >
            <LockPersonOutlinedIcon sx={{ fontSize: 48 }} />
          </Avatar>
          <Typography variant="h5" component="h1">
            Verification Required
          </Typography>
        </Box>

        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {message || "We've sent a verification code to your email because you're logging in from a new location."}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Logging in as: <strong>{username}</strong>
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <TextField
              label="Verification Code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter code (e.g., sun-fox)"
              required
              fullWidth
              autoFocus
              margin="normal"
              variant="outlined"
              disabled={isLoading}
              helperText="Enter the code from your email (lowercase)"
              inputProps={{
                style: { textTransform: 'lowercase' }
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              • Code expires in 15 minutes<br />
              • You have {attemptsRemaining} attempts remaining
            </Typography>

            <CardActions sx={{ justifyContent: 'space-between', mt: 2, px: 0 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isLoading}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={isLoading}
                sx={{ minWidth: 100 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </CardActions>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OtpVerification;